import { analysisDisplayText } from '../utils/analysisDisplayText';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit, Save, X, Plus, Trash2, FileText, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { API } from '../App';

const getPromptDisplayName = (prompt) =>
  (prompt?.prompt_id === 'custom_report' || prompt?.key === 'custom_report') ? 'Custom Analysis' :
  prompt?.prompt_id === 'ai_summary_16point' ? 'Client Synthesis' : prompt?.name;

const ReportPromptsManager = ({ showSuccess, showError, showInfo }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${API}/admin/report-prompts`);
      setPrompts(response.data.prompts);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      if (showError) {
        showError('Failed to load Analysis prompts');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prompt) => {
    setEditingPrompt({ ...prompt });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingPrompt) return;
    
    setSaveLoading(true);
    try {
      const response = await axios.put(
        `${API}/admin/report-prompts/${editingPrompt.prompt_id}`,
        {
          name: editingPrompt.name,
          prompt_content: editingPrompt.prompt_content,
          description: editingPrompt.description
        }
      );
      
      if (showSuccess) {
        showSuccess('Analysis prompt updated successfully!');
      }
      
      setIsDialogOpen(false);
      setEditingPrompt(null);
      fetchPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      if (showError) {
        showError(`Failed to save Analysis prompt: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInitialize = async () => {
    try {
      const response = await axios.post(`${API}/admin/report-prompts/initialize`);
      if (response.data.initialized) {
        if (showSuccess) {
          showSuccess(`Successfully initialized ${response.data.count} Analysis prompts!`);
        }
        fetchPrompts();
      } else {
        if (showInfo) {
          showInfo(response.data.message);
        }
      }
    } catch (error) {
      console.error('Error initializing prompts:', error);
      if (showError) {
        showError('Failed to initialize Analysis prompts');
      }
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading Analysis prompts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Analysis Prompts Management</h2>
          <p className="text-gray-600 mt-1">Edit system prompts for each Analysis type independently</p>
        </div>
        {prompts.length === 0 && (
          <Button onClick={handleInitialize} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Initialize Prompts
          </Button>
        )}
      </div>

      {prompts.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium">No Analysis prompts found</p>
              <p className="mt-2">Click "Initialize Prompts" to set up default prompts</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {prompts.map((prompt) => (
            <Card key={prompt.prompt_id} className="hover:shadow-lg transition-shadow" data-testid={`report-prompt-card-${prompt.key || prompt.report_type || prompt.prompt_id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{analysisDisplayText(getPromptDisplayName(prompt))}</CardTitle>
                    {prompt.description && (
                      <p className="text-sm text-gray-600 mt-1">{analysisDisplayText(prompt.description)}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                      {prompt.phase && <span>Phase {prompt.phase}</span>}
                      <span>Type: {prompt.report_type}</span>
                      <span>Version: {prompt.version || 1}</span>
                      {prompt.updated_at && (
                        <span>Updated: {new Date(prompt.updated_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleEdit(prompt)}
                    variant="outline"
                    size="sm"
                    className="ml-4"
                    data-testid={`edit-report-prompt-${prompt.key || prompt.report_type || prompt.prompt_id}`}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Prompt
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 font-mono whitespace-pre-wrap line-clamp-3">
                    {prompt.prompt_content?.substring(0, 200)}...
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!max-w-[98vw] w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Analysis Prompt: {analysisDisplayText(getPromptDisplayName(editingPrompt))}</DialogTitle>
          </DialogHeader>

          {editingPrompt && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Prompt Name</label>
                <Input
                  value={analysisDisplayText(getPromptDisplayName(editingPrompt))}
                  readOnly
                  className="w-full bg-gray-100 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <Input
                  value={editingPrompt.description || ''}
                  readOnly
                  className="w-full bg-gray-100 cursor-not-allowed"
                  placeholder="Brief description of this prompt's purpose"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Prompt Content</label>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Important:</p>
                      <ul className="list-disc ml-4 mt-1 space-y-1">
                        <li>Changes will affect ALL future Analysis generations of this type</li>
                        <li>Test thoroughly before saving to production</li>
                        <li>Use clear, specific instructions for the AI model</li>
                        <li>Include necessary formatting and structure requirements</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <Textarea
                  value={editingPrompt.prompt_content}
                  onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_content: e.target.value })}
                  className="w-full font-mono text-sm"
                  rows={20}
                  placeholder="Enter the complete system prompt for this Analysis type..."
                  data-testid="report-prompt-content-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Characters: {editingPrompt.prompt_content?.length || 0}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setEditingPrompt(null);
              }}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveLoading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-report-prompt-button"
            >
              {saveLoading ? (
                'Saving...'
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportPromptsManager;
